import customURLConverter from "@/utils/editor/tinymce/customURLConverter";
import kontextlabAssets from "@/utils/editor/tinymce/media-assets/kontextlab-assets";
import kontextlabAccordion from '@/utils/editor/tinymce/nested-accordion/kontextlab-accordion';
import kontextlabQuiz from "@/utils/editor/tinymce/quiz/kontextlab-quiz";
import kontextlabExternal from "@/utils/editor/tinymce/external/kontextlab-external";
import kontextlabKaia from "@/utils/editor/tinymce/kaia/kontextlab-kaia";
import langLink from "@/utils/editor/tinymce/lang-link/lang-link";
import {
  EXTERNAL_CONNECTION_ATTR,
  EXTERNAL_ID_ATTR,
  EXTERNAL_PROVIDER_ATTR,
  externalFrameHtml
} from "@/utils/editor/externalFrame";
const host = window.location.host;
const prefix = host.includes('staging') ? '/editor-next' : '';

export default function (fonts){
  const tinyApiKey = 'no-api-key';// 'uace9c8c9r8lltqn8oiq8sg6sez25p8ig4oc2i1zr6jalckn';
  const tinyBaseSettings = {
    base_url: '/tinymce',
    height: 300,
    width: '100%',
    statusbar: true,
    resize: true,
    font_family_formats: fonts ? getTinyFonts(fonts) : null,
    // skin: false,
    promotion: false,
    content_css: fonts ?
      `
      ${setTinyFontSources(fonts)}, 
      ${prefix}/tinymce/skins/content/default/content.min.css,
       ${prefix}/tinymce/skins/ui/oxide/content.min.css,
       ${prefix}/tinymce/content/content.css` :
      `
      ${prefix}/tinymce/skins/content/default/content.min.css,
       ${prefix}/tinymce/skins/ui/oxide/content.min.css,
       ${prefix}/tinymce/content/content.css`,
    // data-external-source is the placeholder's former provider attribute;
    // kept so content written before the rename still round-trips.
    extended_valid_elements: `span[*],script[src|async|defer|type|charset],i[*],img[*],svg[*],path[*],div[style|class|id|${EXTERNAL_ID_ATTR}|${EXTERNAL_PROVIDER_ATTR}|${EXTERNAL_CONNECTION_ATTR}|data-external-source|data-quiz-id|data-lottie-src|contenteditable]`,
    urlconverter_callback: customURLConverter,
    menu: {
      edit: {
        title: 'Edit', items: 'undo redo | cut copy paste pastetext | selectall | searchreplace'
      },
      view: {
        title: 'View', items: ''
      },
      format: {
        title: 'Format', items: 'bold italic underline strikethrough superscript subscript | align | removeformat'
      },
      insert: {
        title: 'Insert', items: 'link media-assets image media | hr | accordion'
      },
    },
    table_toolbar: '',
    paste_data_images: true,
    entity_encoding: 'raw',
    toolbar_mode: 'sliding',
    font_size_formats: '8px 9px 10px 11px 12px 13px 14px 15px 16px 17px 18px 19px 20px 21px 22px 23px 24px 25px 40px 50px 70px 100px',
    // The stock inline link panel is the one route that bypasses the mceLink
    // command, so it would offer editing without the map picker.
    link_context_toolbar: false,
    plugins: 'autolink code fullscreen image lists link media preview table searchreplace',
    toolbar: 'bold italic underline | link code media-assets image media searchreplace',
    setup: editor => {}
  };
  const tinyToolbar = {
    font: 'fontfamily | fontsize | ',
    fontStyle: 'bold italic underline | ',
    color: 'forecolor backcolor | ',
    list: 'numlist bullist accordionk | ',
    link: 'link code media-assets quiz external-content',
  }

  // COLLECT/IMPLEMENT FONTS
  function getTinyFonts(fonts) {
    let fontList = '';
    fonts.forEach(font => {
      fontList += `${font.name}=${font.family};`;
    })
    return fontList;
  }
  function setTinyFontSources(fonts){
    // prepare fonts source for tinymce init
    // > import fonts into iframe, otherwise visual font changes do not appear
    let fontsSource = '';
    const uniqueFontSources = new Set(fonts.map(font => font.source));
    uniqueFontSources.forEach(source => {
      fontsSource += (source.includes('http') ? '' : import.meta.env.VITE_ROOT_HOST) + source.replace(/,/g, '%2C') + ',';
    })
    fontsSource = fontsSource.substring(0, fontsSource.length - 1);
    return fontsSource;
  }

  // MEDIA ASSETS
  function wrapUrlInHtml(url, text){
    if(url.endsWith('.pdf')) return `<a target="_blank" href="${url}">${text || 'PDF file'}</a>`;
    if((/.(mp4|m4v|mov|avi|flv|webm|flac|mka|m4a|aac|ogg)$/i).test(url)) return `<video controls="controls"><source src="${url}"></video>`;
    if((/\.(lot|lottie|lottie\.json)$/i).test(url)){
      const lottiePath = new URL(url, window.location.origin).pathname;
      return `<div contenteditable="false" class="lottie-frame" data-lottie-src="${lottiePath}"><img src="${prefix}/tinymce/content/lottie-placeholder.svg" alt="Lottie animation" /></div>`;
    }
    return `<img src="${url}" alt="" />`;
  }
  function appendMediaContent({url, editorID, callback, sameCaller}){
    if(!sameCaller) return;
    const editor = window.tinymce.get(editorID);
    const selectedText = editor.selection.getContent({format: 'text'});
    if(editor){
      const htmlElement = wrapUrlInHtml(url, selectedText);
      editor.insertContent(htmlElement);
      if(callback) callback();
    }
  }

  // QUIZ
  function appendQuizContent({id, url, title, editorID, callback, sameCaller}){
    if(!sameCaller) return;
    const editor = window.tinymce.get(editorID);
    if(editor){
      const media =
        url ?
          `<img src="${url}" />` :
          `<svg viewBox="0 0 512 512" width="50" height="50"><path d="M512 288c0 35.35-21.49 64-48 64c-32.43 0-31.72-32-55.64-32C394.9 320 384 330.9 384 344.4V480c0 17.67-14.33 32-32 32h-71.64C266.9 512 256 501.1 256 487.6C256 463.1 288 464.4 288 432c0-26.51-28.65-48-64-48s-64 21.49-64 48c0 32.43 32 31.72 32 55.64C192 501.1 181.1 512 167.6 512H32c-17.67 0-32-14.33-32-32v-135.6C0 330.9 10.91 320 24.36 320C48.05 320 47.6 352 80 352C106.5 352 128 323.3 128 288S106.5 223.1 80 223.1c-32.43 0-31.72 32-55.64 32C10.91 255.1 0 245.1 0 231.6v-71.64c0-17.67 14.33-31.1 32-31.1h135.6C181.1 127.1 192 117.1 192 103.6c0-23.69-32-23.24-32-55.64c0-26.51 28.65-47.1 64-47.1s64 21.49 64 47.1c0 32.43-32 31.72-32 55.64c0 13.45 10.91 24.36 24.36 24.36H352c17.67 0 32 14.33 32 31.1v71.64c0 13.45 10.91 24.36 24.36 24.36c23.69 0 23.24-32 55.64-32C490.5 223.1 512 252.7 512 288z"/></svg>`
      const html = `
					<div contenteditable=false class="quiz-frame" data-quiz-id="${id}">
						${media}
						<p>${title}</p>
					</p>
				`;
      editor.insertContent(html);
      if(callback) callback();
    }
  }

  // external content
  function appendExternalContent({id, url, target, title, provider, connection, source, editorID, callback, sameCaller}){
    if(!sameCaller) return;
    const editor = window.tinymce.get(editorID);
    if(editor){
      const html = externalItemHtml({id, provider, connection, title, source});
      if(!target) editor.insertContent(html);
      if(target) target.outerHTML = html;
      if(callback) callback();
    }
  }
  function externalItemHtml({id, provider, connection, title, source}){
    return externalFrameHtml({id, provider, connection, title, source});
  }

  return {
    tinyApiKey,
    tinyBaseSettings,
    tinyToolbar,
    kontextlabAssets,
    kontextlabAccordion,
    kontextlabQuiz,
    kontextlabExternal,
    kontextlabKaia,
    langLink,
    appendMediaContent,
    appendQuizContent,
    appendExternalContent,
    externalItemHtml
  }
}